import { Injectable, inject, signal, computed } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  Container,
  Image,
  MetadataServiceClient,
} from '../api/metadata-service/auto-generated-client';

interface KojiConfig {
  kojiInstance: string;
  kojiFilesInstance: string;
}

export interface RpmBuildEnvironmentOption {
  name: string;
  branch: string;
  target: string;
  official: boolean;
}

export interface ImageBuildEnvironmentOption {
  name: string;
  branch: string;
  target: string;
  type: 'image' | 'container';
  repoUri: string;
  productPath: string;
  projectPath: string;
  official: boolean;
}

export type OfficialImageOption = Image | Container;

@Injectable()
export class MetadataConfigService {
  private readonly metadataClient = inject(MetadataServiceClient);

  private readonly _kojiConfig = signal<KojiConfig>({
    kojiInstance: '',
    kojiFilesInstance: '',
  });

  readonly kojiConfig = this._kojiConfig.asReadonly();

  private readonly _rpmBuildEnvironments = signal<RpmBuildEnvironmentOption[]>(
    []
  );
  readonly rpmBuildEnvironments = this._rpmBuildEnvironments.asReadonly();

  /** Official-only RPM build-environments, for the official package build scenario. */
  readonly officialRpmBuildEnvironments = computed(() =>
    this._rpmBuildEnvironments().filter(environment => environment.official)
  );

  private readonly _imageBuildEnvironments = signal<
    ImageBuildEnvironmentOption[]
  >([]);
  /** All image/container build-environments (official and unofficial), for the non-official image build scenario. */
  readonly imageBuildEnvironments = this._imageBuildEnvironments.asReadonly();

  /** Official-only image/container build-environments, for the official image build scenario. */
  readonly officialImageBuildEnvironments = computed(() =>
    this._imageBuildEnvironments().filter(environment => environment.official)
  );

  private readonly _buildEnvironmentsLoading = signal(true);
  readonly buildEnvironmentsLoading =
    this._buildEnvironmentsLoading.asReadonly();

  private readonly _buildEnvironmentsError = signal<string | null>(null);
  readonly buildEnvironmentsError = this._buildEnvironmentsError.asReadonly();

  constructor() {
    this.loadKojiConfig();
    this.loadBuildEnvironments();
  }

  getImagesForEnvironment(
    environment: ImageBuildEnvironmentOption
  ): Observable<OfficialImageOption[]> {
    const params = [
      environment.repoUri,
      undefined,
      environment.branch,
      environment.productPath,
      environment.repoUri,
      undefined,
      environment.branch,
      environment.projectPath,
    ] as const;

    return environment.type === 'container'
      ? this.metadataClient
          .metadata_GetContainers(...params)
          .pipe(map(response => response.containers ?? []))
      : this.metadataClient
          .metadata_GetImages(...params)
          .pipe(map(response => response.images ?? []));
  }

  private loadKojiConfig(): void {
    this.metadataClient.config_GetKojiConfig().subscribe({
      next: config => {
        this._kojiConfig.set({
          kojiInstance: config.kojiInstance ?? '',
          kojiFilesInstance: config.kojiFilesInstance ?? '',
        });
      },
      error: err => {
        console.error('Failed to load config from metadata service', err);
      },
    });
  }

  private loadBuildEnvironments(): void {
    this._buildEnvironmentsLoading.set(true);
    this._buildEnvironmentsError.set(null);

    this.metadataClient.config_Dump().subscribe({
      next: response => {
        const distros = response.config?.distros ?? {};

        // Build environments are defined per Control Tower environment; the dump only ever
        // contains this deployment's own environment. Each entry carries the distro/version
        // it belongs to so it can still reach that version's git-source-repos map.
        const namedEnvironments = Object.values(
          response.config?.environments ?? {}
        ).flatMap(ctEnvironment =>
          Object.entries(ctEnvironment.buildEnvironments ?? {}).flatMap(
            ([name, buildEnvironments]) =>
              buildEnvironments.map(environment => ({ name, environment }))
          )
        );

        const rpmEnvironments = namedEnvironments
          .filter(
            ({ environment, name }) =>
              environment.type?.toLowerCase() === 'rpm' && !!name
          )
          .map(({ name, environment }) => ({
            name,
            branch: environment.branch ?? '',
            target: environment.target ?? '',
            official: environment.official ?? false,
          }))
          .filter(
            (environment, index, all) =>
              all.findIndex(item => item.name === environment.name) === index
          )
          .sort((left, right) => left.name.localeCompare(right.name));

        const imageEnvironments = namedEnvironments
          .filter(({ environment, name }) => {
            const type = environment.type?.toLowerCase();
            return !!name && (type === 'image' || type === 'container');
          })
          .map(({ name, environment }) => {
            const version =
              distros[environment.distro ?? '']?.versions?.[
                environment.version ?? ''
              ];
            const sourceRepo = (version?.gitSourceRepos?.[
              environment.repo ?? ''
            ] ?? [])[0];
            return {
              name,
              branch: environment.branch ?? '',
              target: environment.target ?? '',
              type: environment.type!.toLowerCase() as 'image' | 'container',
              repoUri: sourceRepo?.ref ?? '',
              productPath: sourceRepo?.productPath ?? '',
              projectPath: sourceRepo?.projectPath ?? '',
              official: environment.official ?? false,
            };
          })
          .filter(
            (environment, index, all) =>
              all.findIndex(item => item.name === environment.name) === index
          )
          .sort((left, right) => left.name.localeCompare(right.name));

        this._rpmBuildEnvironments.set(rpmEnvironments);
        this._imageBuildEnvironments.set(imageEnvironments);
        this._buildEnvironmentsLoading.set(false);
      },
      error: error => {
        console.error('Failed to load build environments from metadata', error);
        this._buildEnvironmentsError.set(
          'Failed to load build environments from distro-config.'
        );
        this._buildEnvironmentsLoading.set(false);
      },
    });
  }
}
